import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  Paper,
  Button,
  Stack,
  Snackbar,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { useLocation } from "react-router-dom";
import EditableDisplayTable from "../../components/table/EditableDisplayTable";
import { fetchTenantDetails } from "../../apis/tenantApi/tenantApi";
import { saveTemplateChanges } from "../../apis/templateApi/baseTemplateView";

// --- Interfaces (Keep as is) ---
export interface ClientTemplateDetail {
  key: string;
  value: string;
  env?: string;
  flag?: "delete";
}

export interface ClientConfiguration {
  id: number;
  clientId: number;
  clientEnvConfigId: number;
  configUrl: string;
  serviceType: string;
  azureZone: string;
  clientSize: number;
  clientInfo: ClientTemplateDetail[];
  configurations: ClientTemplateDetail[];
  services: ClientTemplateDetail[];
  dynamicData: ClientTemplateDetail[] | null;
  dynamicDataType: string | null;
  loading: boolean;
  error: string | null;
}

interface ClientChanges {
  configInstanceId: number;
  serviceType: string;
  changedClientInfo: ClientTemplateDetail[];
  changedConfigurations: ClientTemplateDetail[];
  changedServices: ClientTemplateDetail[];
  changedDynamicData: ClientTemplateDetail[];
}

interface FormattedClientApiData {
  base?: ClientTemplateDetail[];
  [serviceType: string]: any;
}

const findDifferences = (
  original: ClientTemplateDetail[],
  edited: ClientTemplateDetail[]
): ClientTemplateDetail[] => {
  const changes: ClientTemplateDetail[] = [];
  const originalMap = new Map(original.map((item) => [item.key, item]));
  const editedMap = new Map(edited.map((item) => [item.key, item]));
  edited.forEach((editedItem) => {
    const originalItem = originalMap.get(editedItem.key);
    if (!originalItem) {
      changes.push({ ...editedItem });
    } else if (
      originalItem.value !== editedItem.value ||
      originalItem.env !== editedItem.env
    ) {
      changes.push({ ...editedItem });
    }
  });
  original.forEach((originalItem) => {
    if (!editedMap.has(originalItem.key)) {
      changes.push({ ...originalItem, flag: "delete" });
    }
  });
  return changes;
};

const useClientConfiguration = () => {
  const location = useLocation();
  const [clientConfigurations, setClientConfigurations] = useState<
    ClientConfiguration[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string | null>(null);
  const [clientData, setClientData] = useState<{clientName: string; envName: string, domainURL: string}>()

  useEffect(() => {
    const fetchClientConfigs = async () => {
      setIsLoading(true);
      setError(null);
      setClientConfigurations([]);
      const path = location.pathname;
      const pathSegments = path.split("/");
      const clientIdSegment = pathSegments[pathSegments.length - 1];
      const currentTenantId = decodeURIComponent(clientIdSegment.trim());
      const clientIdNum = parseInt(currentTenantId, 10);

      if (isNaN(clientIdNum)) {
        setError("Invalid Client ID found in URL");
        setIsLoading(false);
        return;
      }
      setTenantId(currentTenantId);

      try {
        const response = await fetchTenantDetails(clientIdNum);
        if(response.success) setClientData(response.client_data)
        if (response.success && Array.isArray(response.data)) {
          const configs: ClientConfiguration[] = response.data.map(
            (item: any): ClientConfiguration => {
              const serviceType = item.ClientService_Typ || "UNKNOWN";
              let clientInfoData: ClientTemplateDetail[] = [];
              let configurationsData: ClientTemplateDetail[] = [];
              let servicesData: ClientTemplateDetail[] = [];
              let dynamicData: ClientTemplateDetail[] | null = null;
              let dynamicDataType: string | null = null;

              if (
                serviceType === "BASE" &&
                typeof item.clientTemplateContent === "object" &&
                !Array.isArray(item.clientTemplateContent)
              ) {
                clientInfoData =
                  item.clientTemplateContent?.client_info?.data?.map(
                    (d: any) => ({ ...d })
                  ) || [];
                configurationsData =
                  item.clientTemplateContent?.configurations?.data?.map(
                    (d: any) => ({ ...d })
                  ) || [];
                servicesData =
                  item.clientTemplateContent?.services?.data?.map((d: any) => ({
                    ...d,
                  })) || [];
              } else if (
                serviceType !== "BASE" &&
                Array.isArray(item.clientTemplateContent) &&
                item.clientTemplateContent.length > 0
              ) {
                const contentObject = item.clientTemplateContent[0];
                const keys = Object.keys(contentObject);
                if (keys.length === 1) {
                  const typeKey = keys[0];
                  dynamicDataType = typeKey;
                  dynamicData =
                    contentObject[typeKey]?.data?.map((d: any) => ({ ...d })) ||
                    [];
                  if (typeKey !== serviceType.toLowerCase()) {
                    console.warn(
                      `Mismatch between ServiceType (${serviceType}) and key (${typeKey}) in clientTemplateContent for ID ${item.Id}`
                    );
                  }
                } else {
                  console.warn(
                    `Expected single key in clientTemplateContent array for non-BASE type, found ${keys.length} for ID ${item.Id}`
                  );
                }
              } else {
                console.warn(
                  `Unexpected clientTemplateContent structure for ServiceType "${serviceType}" and ID ${item.Id}`
                );
              }

              return {
                id: item.Id,
                clientId: item.Client_Id,
                clientEnvConfigId: item.ClientEnvConfig_Id,
                configUrl: item.ClientConfig_Url || "N/A",
                serviceType: serviceType,
                azureZone: item.ClientAzure_Zn || "N/A",
                clientSize: item.Client_Size || 0,
                clientInfo: clientInfoData,
                configurations: configurationsData,
                services: servicesData,
                dynamicData: dynamicData,
                dynamicDataType: dynamicDataType,
                loading: false,
                error: null,
              };
            }
          );
          setClientConfigurations(configs);
        } else {
          throw new Error(
            response.message ||
              `Failed to fetch valid data for Client ID ${clientIdNum}`
          );
        }
      } catch (err: any) {
        console.error(
          `Error fetching client configurations for ID ${clientIdNum}:`,
          err
        );
        setError(`Error fetching data: ${err.message || "Unknown error"}`);
      } finally {
        setIsLoading(false);
      }
    };

    fetchClientConfigs();
  }, [location.pathname]);

  const formatChangesForApi = (
    allChanges: ClientChanges[]
  ): FormattedClientApiData => {
    const formattedData: FormattedClientApiData = {};
    allChanges.forEach((change) => {
      const serviceTypeKey = change.serviceType.toLowerCase();

      if (change.serviceType === "BASE") {
        if (!formattedData.base) {
          formattedData.base = [];
        }
        if (change.changedClientInfo.length > 0) {
          formattedData.base = [
            ...(formattedData.base || []),
            ...change.changedClientInfo,
          ];
        }
        if (change.changedConfigurations.length > 0) {
          formattedData.base = [
            ...(formattedData.base || []),
            ...change.changedConfigurations,
          ];
        }
        if (change.changedServices.length > 0) {
          formattedData.base = [
            ...(formattedData.base || []),
            ...change.changedServices,
          ];
        }
      } else {
        if (change.changedDynamicData.length > 0) {
          if (!formattedData[serviceTypeKey]) {
            formattedData[serviceTypeKey] = [];
          }
          formattedData[serviceTypeKey].push(...change.changedDynamicData);
        }
      }
    });

    if (formattedData.base && Object.keys(formattedData.base).length === 0) {
      delete formattedData.base;
    }
    Object.keys(formattedData).forEach((key) => {
      if (
        key !== "base" &&
        Array.isArray(formattedData[key]) &&
        formattedData[key].length === 0
      ) {
        delete formattedData[key];
      }
    });
    return formattedData;
  };

  const applyChangesToState = (savedChanges: ClientChanges[]) => {
    setClientConfigurations((currentConfigs) => {
      return currentConfigs.map((config) => {
        const changeData = savedChanges.find(
          (c) => c.configInstanceId === config.id
        );
        if (!changeData) return config;

        const updateDetailArray = (
          originalArray: ClientTemplateDetail[],
          changes: ClientTemplateDetail[]
        ): ClientTemplateDetail[] => {
          const updatedArray = [...originalArray];
          changes.forEach((change) => {
            const existingIndex = updatedArray.findIndex(
              (item) => item.key === change.key
            );
            const { flag, ...changeWithoutFlag } = change;
            if (flag === "delete") {
              if (existingIndex !== -1) updatedArray.splice(existingIndex, 1);
            } else if (existingIndex !== -1) {
              updatedArray[existingIndex] = changeWithoutFlag;
            } else {
              updatedArray.push(changeWithoutFlag);
            }
          });
          return updatedArray;
        };

        let updatedClientInfo = config.clientInfo;
        let updatedConfigurations = config.configurations;
        let updatedServices = config.services;
        let updatedDynamicData = config.dynamicData;

        if (config.serviceType === "BASE") {
          updatedClientInfo = updateDetailArray(
            config.clientInfo,
            changeData.changedClientInfo
          );
          updatedConfigurations = updateDetailArray(
            config.configurations,
            changeData.changedConfigurations
          );
          updatedServices = updateDetailArray(
            config.services,
            changeData.changedServices
          );
        } else if (config.dynamicData) {
          updatedDynamicData = updateDetailArray(
            config.dynamicData,
            changeData.changedDynamicData
          );
        }

        return {
          ...config,
          clientInfo: updatedClientInfo,
          configurations: updatedConfigurations,
          services: updatedServices,
          dynamicData: updatedDynamicData,
        };
      });
    });
  };

  const handleSaveClientChanges = async (changesToSave: ClientChanges[]) => {
    if (!tenantId) {
      throw new Error("Tenant ID is not available for saving.");
    }
    try {
      const formattedData: FormattedClientApiData =
        formatChangesForApi(changesToSave);
      if (Object.keys(formattedData).length === 0) {
        console.log("No changes detected to save.");
        return { success: true, message: "No changes detected." };
      }
      const body = {
        clientInfo: {
          tenantId: clientData?.clientName,
          envId: `${clientData!.envName.toLowerCase()}`,
          domain: clientData!.domainURL
        },
        ...formattedData
      }
      const response = await saveTemplateChanges(tenantId, body);
      if (response.success) {
        applyChangesToState(changesToSave);
        return response;
      } else {
        throw new Error(response.message || "Failed to save changes via API");
      }
    } catch (error) {
      console.error("Error in handleSaveClientChanges:", error);
      throw error;
    }
  };

  return {
    clientConfigurations,
    isLoading,
    error,
    saveClientChanges: handleSaveClientChanges,
    tenantId,
  };
};

type LocalEditDataBase = {
  clientInfo: ClientTemplateDetail[];
  configurations: ClientTemplateDetail[];
  services: ClientTemplateDetail[];
};
type LocalEditDataDynamic = {
  dynamicData: ClientTemplateDetail[];
};
type LocalEditData = LocalEditDataBase | LocalEditDataDynamic | null;
type DataType = "clientInfo" | "configurations" | "services" | "dynamicData";

interface ClientServiceCardProps {
  clientConfig: ClientConfiguration;
  isPageEditMode: boolean;
  registerChangeCollector: (
    configInstanceId: number,
    collector: () => ClientChanges | null
  ) => void;
  unregisterChangeCollector: (configInstanceId: number) => void;
  onInitialized: (configInstanceId: number) => void;
}

const ClientServiceCard = React.memo(
  ({
    clientConfig,
    isPageEditMode,
    registerChangeCollector,
    unregisterChangeCollector,
    onInitialized,
  }: ClientServiceCardProps) => {
    const [localEditData, setLocalEditData] = useState<LocalEditData>(null);
    const isEditingThisCard = isPageEditMode && localEditData !== null;
    const isBaseType = clientConfig.serviceType === "BASE";
    const cardIdRef = useRef(clientConfig.id);

    useEffect(() => {
      const currentCardId = cardIdRef.current;
      if (isPageEditMode) {
        if (localEditData === null) {
          setTimeout(() => {
            let initialData: LocalEditData = null;
            try {
              if (isBaseType) {
                initialData = {
                  clientInfo: clientConfig.clientInfo.map((item) => ({
                    ...item,
                  })),
                  configurations: clientConfig.configurations.map((item) => ({
                    ...item,
                  })),
                  services: clientConfig.services.map((item) => ({ ...item })),
                };
              } else if (clientConfig.dynamicData) {
                initialData = {
                  dynamicData: clientConfig.dynamicData.map((item) => ({
                    ...item,
                  })),
                };
              }
              setLocalEditData(initialData);

              onInitialized(currentCardId);
            } catch (error) {
              console.error(`Error initializing card ${currentCardId}:`, error);
              onInitialized(currentCardId);
            }
          }, 0);
        }
      } else {
        if (localEditData !== null) {
          setLocalEditData(null);
        }
      }
    }, [
      isPageEditMode,
      clientConfig,
      isBaseType,
      localEditData,
      onInitialized,
    ]);

    const handleDataChange = useCallback(
      (dataType: DataType, updatedData: ClientTemplateDetail[]) => {
        setTimeout(() => {
          setLocalEditData((prev) => {
            if (!prev) return null;
            if (dataType === "dynamicData" && "dynamicData" in prev) {
              return { ...prev, dynamicData: [...updatedData] };
            } else if (dataType !== "dynamicData" && "clientInfo" in prev) {
              return { ...prev, [dataType]: [...updatedData] };
            }
            console.error(
              "Mismatched data type and local state structure in handleDataChange"
            );
            return prev;
          });
        }, 0);
      },
      []
    );

    const getChanges = useCallback((): ClientChanges | null => {
      if (!isPageEditMode || !localEditData) return null;

      let changedClientInfo: ClientTemplateDetail[] = [];
      let changedConfigurations: ClientTemplateDetail[] = [];
      let changedServices: ClientTemplateDetail[] = [];
      let changedDynamicData: ClientTemplateDetail[] = [];
      let hasChanges = false;

      if (isBaseType && "clientInfo" in localEditData) {
        changedClientInfo = findDifferences(
          clientConfig.clientInfo,
          localEditData.clientInfo
        );
        changedConfigurations = findDifferences(
          clientConfig.configurations,
          localEditData.configurations
        );
        changedServices = findDifferences(
          clientConfig.services,
          localEditData.services
        );
        hasChanges =
          changedClientInfo.length > 0 ||
          changedConfigurations.length > 0 ||
          changedServices.length > 0;
      } else if (
        !isBaseType &&
        "dynamicData" in localEditData &&
        clientConfig.dynamicData
      ) {
        changedDynamicData = findDifferences(
          clientConfig.dynamicData,
          localEditData.dynamicData
        );
        hasChanges = changedDynamicData.length > 0;
      }

      if (hasChanges) {
        return {
          configInstanceId: clientConfig.id,
          serviceType: clientConfig.serviceType,
          changedClientInfo,
          changedConfigurations,
          changedServices,
          changedDynamicData,
        };
      }
      return null;
    }, [isPageEditMode, localEditData, clientConfig, isBaseType]);

    useEffect(() => {
      if (isPageEditMode) {
        registerChangeCollector(clientConfig.id, getChanges);
      } else {
        unregisterChangeCollector(clientConfig.id);
      }
      return () => {
        unregisterChangeCollector(clientConfig.id);
      };
    }, [
      isPageEditMode,
      clientConfig.id,
      registerChangeCollector,
      unregisterChangeCollector,
      getChanges,
    ]);

    const clientInfoColumns = useMemo(
      () => [
        { header: "Key", key: "key" },
        { header: "Value", key: "value" },
      ],
      []
    );
    const configurationColumns = useMemo(
      () => [
        { header: "Key", key: "key" },
        { header: "Value", key: "value" },
        {
          header: "Env",
          key: "env",
          editable: false,
          render: (v: any) =>
            v ? (
              <Chip size="small" label={v} color="secondary" />
            ) : (
              <span>-</span>
            ),
        },
      ],
      []
    );
    const serviceColumns = useMemo(
      () => [
        { header: "Key", key: "key" },
        { header: "Value", key: "value" },
      ],
      []
    );
    const dynamicColumns = useMemo(
      () => [
        { header: "Key", key: "key" },
        { header: "Value", key: "value" },
        {
          header: "Env",
          key: "env",
          editable: false,
          render: (v: any) =>
            v ? <Chip size="small" label={v} color="info" /> : <span>-</span>,
        },
      ],
      []
    );

    let clientInfoData: ClientTemplateDetail[] = clientConfig.clientInfo;
    let configurationsData: ClientTemplateDetail[] =
      clientConfig.configurations;
    let servicesData: ClientTemplateDetail[] = clientConfig.services;
    let dynamicData: ClientTemplateDetail[] | null = clientConfig.dynamicData;
    if (isEditingThisCard && localEditData) {
      if (isBaseType && "clientInfo" in localEditData) {
        clientInfoData = localEditData.clientInfo;
        configurationsData = localEditData.configurations;
        servicesData = localEditData.services;
      } else if (!isBaseType && "dynamicData" in localEditData) {
        dynamicData = localEditData.dynamicData;
      }
    }

    if (clientConfig.error) {
      return (
        <Alert severity="error">
          Error loading config: {clientConfig.error}
        </Alert>
      );
    }

    return (
      <Card
        key={clientConfig.id}
        sx={{ mb: 3, borderRadius: 2, overflow: "visible" }}
      >
        <CardContent>
          <Box sx={{ mb: 2, overflow: "auto" }}>
            <Typography variant="h5" color="primary" sx={{ fontWeight: 500 }}>
              {clientConfig.serviceType} Configuration (ID: {clientConfig.id})
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3, mt: 1 }}>
              <Typography variant="subtitle2" color="textSecondary">
                <strong>Client ID:</strong> {clientConfig.clientId}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                <strong>Instance ID:</strong> {clientConfig.id}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                <strong>Config Env ID:</strong> {clientConfig.clientEnvConfigId}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                <strong>Zone:</strong> {clientConfig.azureZone}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                <strong>Size:</strong> {clientConfig.clientSize}
              </Typography>
              <Typography
                variant="subtitle2"
                color="textSecondary"
                sx={{ wordBreak: "break-all" }}
              >
                <strong>URL:</strong> {clientConfig.configUrl}
              </Typography>
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />

          {isBaseType ? (
            <>
              <Accordion
                defaultExpanded
                sx={{
                  mb: 2,
                  "&.MuiPaper-root": {
                    boxShadow: "none",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}
                >
                  <Typography variant="h6">Client Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {clientInfoData.length > 0 || isEditingThisCard ? (
                    <EditableDisplayTable
                      columns={clientInfoColumns}
                      data={clientInfoData}
                      isEditable={false}
                      onDataChange={(d) => handleDataChange("clientInfo", d)} 
                      loading={false}
                    />
                  ) : (
                    <Alert severity="info">
                      No client information available.
                    </Alert>
                  )}
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  mb: 2,
                  "&.MuiPaper-root": {
                    boxShadow: "none",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}
                >
                  <Typography variant="h6">Configurations</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {configurationsData.length > 0 || isEditingThisCard ? (
                    <EditableDisplayTable
                      columns={configurationColumns}
                      data={configurationsData}
                      isEditable={isEditingThisCard}
                      onDataChange={(d) =>
                        handleDataChange("configurations", d)
                      }
                      loading={false}
                    /> // [cite: 107]
                  ) : (
                    <Alert severity="info">
                      No configuration details available.
                    </Alert>
                  )}
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  "&.MuiPaper-root": {
                    boxShadow: "none",
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}
                >
                  <Typography variant="h6">Services</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {servicesData.length > 0 || isEditingThisCard ? (
                    <EditableDisplayTable
                      columns={serviceColumns}
                      data={servicesData}
                      isEditable={false}
                      onDataChange={(d) => handleDataChange("services", d)}
                      loading={false}
                    />
                  ) : (
                    <Alert severity="info">No service details available.</Alert>
                  )}
                </AccordionDetails>
              </Accordion>
            </>
          ) : (
            <Accordion
              defaultExpanded
              sx={{
                "&.MuiPaper-root": {
                  boxShadow: "none",
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                },
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ backgroundColor: "rgba(0, 0, 0, 0.03)" }}
              >
                <Typography variant="h6">
                  {clientConfig.dynamicDataType
                    ? `${clientConfig.dynamicDataType.toUpperCase()} Settings`
                    : "Details"}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {(dynamicData && dynamicData.length > 0) ||
                (isEditingThisCard &&
                  localEditData &&
                  "dynamicData" in localEditData) ? (
                  <EditableDisplayTable
                    columns={dynamicColumns}
                    data={dynamicData ?? []}
                    isEditable={isEditingThisCard && !!dynamicData}
                    onDataChange={(d) => handleDataChange("dynamicData", d)}
                    loading={false}
                  />
                ) : (
                  <Alert severity="info">
                    No specific details available for {clientConfig.serviceType}
                    .
                  </Alert>
                )}{" "}
                {/* [cite: 114] */}
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>
    );
  }
);

const ClientTemplateView: React.FC = () => {
  const {
    clientConfigurations,
    isLoading,
    error,
    saveClientChanges,
    tenantId,
  } = useClientConfiguration();
  const [isEditMode, setIsEditMode] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });
  const changeCollectorsRef = useRef<Map<number, () => ClientChanges | null>>(
    new Map()
  );
  const [initializedCards, setInitializedCards] = useState<Set<number>>(
    new Set()
  );

  const registerChangeCollector = useCallback(
    (configInstanceId: number, collector: () => ClientChanges | null) => {
      changeCollectorsRef.current.set(configInstanceId, collector);
    },
    []
  );
  const unregisterChangeCollector = useCallback((configInstanceId: number) => {
    changeCollectorsRef.current.delete(configInstanceId);
  }, []);

  const handleEnterEditMode = useCallback(() => {
    if (clientConfigurations.length > 0) {
      setIsEditMode(true);
      setIsEditLoading(true);
      setInitializedCards(new Set());
    } else {
      setNotification({
        open: true,
        message: "No configurations available to edit.",
        severity: "info",
      });
    }
  }, [clientConfigurations.length]);

  const handleCardInitialized = useCallback((configId: number) => {
    setInitializedCards((prev) => {
      const newSet = new Set(prev);
      newSet.add(configId);
      return newSet;
    });
  }, []);

  useEffect(() => {
    if (isEditLoading && clientConfigurations.length > 0) {
      if (initializedCards.size === clientConfigurations.length) {
        setIsEditLoading(false);
      }
    }
    if (!isEditMode && isEditLoading) {
      setIsEditLoading(false);
    }
  }, [
    initializedCards,
    clientConfigurations.length,
    isEditLoading,
    isEditMode,
  ]); // Dependencies

  const handleSaveChanges = useCallback(async () => {
    setIsSaving(true);
    try {
      const allChanges: ClientChanges[] = [];
      changeCollectorsRef.current.forEach((collector) => {
        const changes = collector();
        if (changes) {
          allChanges.push(changes);
        }
      });

      if (allChanges.length === 0) {
        setNotification({
          open: true,
          message: "No changes were made.",
          severity: "info",
        });
        setIsEditMode(false);
        changeCollectorsRef.current.clear();
        setIsSaving(false);
        return;
      }

      console.log("Collected changes to save:", allChanges);
      const response = await saveClientChanges(allChanges);

      if (response.success) {
        setNotification({
          open: true,
          message: response.message || "Changes saved successfully!",
          severity: "success",
        }); // [cite: 124] Show success message
        setIsEditMode(false);
        changeCollectorsRef.current.clear();
      } else {
        throw new Error(response.message || "Failed to save changes.");
      }
    } catch (error: any) {
      console.error("Error saving client changes:", error);
      setNotification({
        open: true,
        message: `Failed to save changes: ${
          error.message || "Please try again."
        }`,
        severity: "error",
      });
    } finally {
      setIsSaving(false);
    }
  }, [saveClientChanges]);

  const handleCancelEdit = useCallback(() => {
    setIsEditMode(false);
    setIsEditLoading(false);
    changeCollectorsRef.current.clear();
    setInitializedCards(new Set());
  }, []);

  const handleCloseNotification = useCallback(() => {
    setNotification((prev) => ({ ...prev, open: false }));
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading client configurations...
        </Typography>
      </Box>
    );
  }
  if (error) {
    return (
      <Paper elevation={3} sx={{ m: 2, p: 3 }}>
        {" "}
        {/* [cite: 132] */}
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>{" "}
        {/* [cite: 132] */}
        <Typography>
          An error occurred while fetching client configuration data. Please
          check the console or try again later.
        </Typography>{" "}
        {/* [cite: 132] */}
      </Paper>
    );
  }

  return (
    <Box sx={{ p: 3, overflow: "auto", position: "relative" }}>
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          backgroundColor: "#f8f9fa",
          position: "sticky",
          top: 0,
          zIndex: 11,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h4" gutterBottom sx={{ mb: 0 }}>
            Client Configuration Details (Tenant: {tenantId || "Loading..."})
          </Typography>
          {!isEditMode ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<EditIcon />}
              onClick={handleEnterEditMode}
              disabled={
                isSaving ||
                isLoading ||
                isEditLoading ||
                clientConfigurations.length === 0
              }
            >
              {" "}
              Edit{" "}
            </Button>
          ) : (
            <Stack direction="row" spacing={2} alignItems="center">
              {isEditLoading && (
                <CircularProgress size={24} title="Preparing edit mode..." />
              )}
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<CancelIcon />}
                onClick={handleCancelEdit}
                disabled={isSaving || isEditLoading}
              >
                {" "}
                Cancel{" "}
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                onClick={handleSaveChanges}
                disabled={isSaving || isEditLoading}
              >
                {isSaving ? (
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                ) : null}
                {isSaving ? "Saving..." : "Save Changes"}
              </Button>
            </Stack>
          )}
        </Box>
      </Paper>

      {isEditLoading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
            flexDirection: "column",
            textAlign: "center",
          }}
        >
          <CircularProgress size={40} />
          <Typography sx={{ mt: 1, color: "text.secondary" }}>
            Preparing edit mode...
          </Typography>
          <Typography variant="caption" sx={{ color: "text.disabled" }}>
            ({initializedCards.size} / {clientConfigurations.length} sections
            ready)
          </Typography>
        </Box>
      )}

      {clientConfigurations.length === 0 && !isLoading ? (
        <Alert severity="info">
          No client configurations found for Tenant ID {tenantId}.
        </Alert>
      ) : (
        clientConfigurations.map((config) => (
          <ClientServiceCard
            key={config.id}
            clientConfig={config}
            isPageEditMode={isEditMode}
            registerChangeCollector={registerChangeCollector}
            unregisterChangeCollector={unregisterChangeCollector}
            onInitialized={handleCardInitialized}
          />
        ))
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
export default ClientTemplateView;
